import { useCallback, useEffect, useMemo, useState } from "react";
import { STAFF_BULK_MAX } from "../staff/staff-rows";
import type { StaffAssignmentRow } from "../staff/types";

interface AssignmentSelection {
  readonly selectedIds: ReadonlySet<string>;
  readonly selectedRows: readonly StaffAssignmentRow[];
  readonly toggle: (entityId: string) => void;
  readonly clear: () => void;
}

/**
 * Checkbox selection over the assignment rows, capped at the API's bulk
 * limit (a row past the cap stays unticked). Entity ids are unique across a
 * user's assignments, so the id alone identifies a row. Ids whose row left
 * the list (a refetch after another admin's change) are dropped, so they
 * neither eat the cap nor come back pre-ticked.
 */
export function useAssignmentSelection(rows: readonly StaffAssignmentRow[]): AssignmentSelection {
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    const present = new Set(rows.map((row) => row.entityId));
    setSelectedIds((prev) => {
      const kept = new Set([...prev].filter((id) => present.has(id)));
      return kept.size === prev.size ? prev : kept;
    });
  }, [rows]);

  const toggle = useCallback((entityId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(entityId)) next.delete(entityId);
      else if (next.size < STAFF_BULK_MAX) next.add(entityId);
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelectedIds(new Set()), []);

  const selectedRows = useMemo(
    () => rows.filter((row) => selectedIds.has(row.entityId)),
    [rows, selectedIds],
  );

  return { selectedIds, selectedRows, toggle, clear };
}
